import {
  UseMutationConfig,
  PreloadedQuery,
  loadQuery,
  usePreloadedQuery,
  fetchQuery,
  Environment,
  LoadQueryOptions,
} from "react-relay";
import {
  MutationParameters,
  Disposable,
  OperationType,
  GraphQLTaggedNode,
  Observable,
  UploadableMap,
  Uploadable,
  Subscribable,
  getFragment,
  getSelector,
  getRefetchableFragment,
  getPendingOperationsForFragment,
} from "relay-runtime";
import { isPlainObject } from "lodash";
import { DeepIsEqual, deepIsEqual } from "./form";
import { useLoaderData } from "react-router";
import { DeepPartial, DeepPartialSkipArrayKey } from "react-hook-form";
import { KeyType, KeyTypeData } from "relay-runtime/store/RelayStoreTypes";
import { invariant } from "./helpers";

export function commitMutationPromise<TMutation extends MutationParameters>(
  func: (config: UseMutationConfig<TMutation>) => Disposable,
): (config: UseMutationConfig<TMutation>) => Promise<TMutation["response"]> {
  return (config) => {
    return new Promise((resolve, reject) => {
      const { onCompleted, onError, ...rest } = config;
      func({
        ...rest,
        onCompleted: (response, errors) => {
          if (onCompleted) {
            onCompleted(response, errors);
          }
          resolve(response);
        },
        onError: (error) => {
          if (onError) {
            onError(error);
          }
          reject(error);
        },
      });
    });
  };
}

export interface PreloadedData<TQuery extends OperationType> {
  graphql: GraphQLTaggedNode;
  variables: TQuery["variables"];
  query: PreloadedQuery<TQuery>;
}

export interface PreloadedDataWithExtra<TQuery extends OperationType, TExtra> {
  graphql: GraphQLTaggedNode;
  variables: TQuery["variables"];
  query: PreloadedQuery<TQuery>;
  extra?: TExtra | null;
}

export const preload = <TQuery extends OperationType>(
  environment: Environment,
  graphql: GraphQLTaggedNode,
  variables: TQuery["variables"] = {},
  options: LoadQueryOptions = { fetchPolicy: "store-and-network" },
): PreloadedData<TQuery> => {
  return {
    graphql,
    variables,
    query: loadQuery<TQuery>(environment, graphql, variables, options),
  };
};

export const preloadWith = async <TQuery extends OperationType, TExtra>(
  environment: Environment,
  query: GraphQLTaggedNode,
  variables: TQuery["variables"],
  extra: (response: TQuery["response"]) => Promise<TExtra>,
): Promise<PreloadedDataWithExtra<TQuery, TExtra>> => {
  const response = await fetchQuery<TQuery>(
    environment,
    query,
    variables,
  ).toPromise();

  if (!response) {
    throw new Error(
      "preloadWith expected `fetchQuery().toPromise()` to return a response payload, but it completed without one.",
    );
  }

  return {
    graphql: query,
    variables,
    query: loadQuery<TQuery>(environment, query, variables, {
      fetchPolicy: "store-only",
    }),
    extra: await extra(response),
  };
};

export const reload = <TQuery extends OperationType>(
  environment: Environment,
  preloaded: PreloadedData<TQuery>,
): PreloadedData<TQuery> => {
  const { graphql, variables, query: _query, ...rest } = preloaded;
  return {
    graphql,
    variables,
    query: loadQuery<TQuery>(environment, graphql, variables),
    ...rest,
  };
};

export const usePreloaded = <TQuery extends OperationType>() => {
  const { variables, query, graphql, ...rest } =
    useLoaderData() as PreloadedData<TQuery>;
  return {
    variables,
    query: usePreloadedQuery<TQuery>(graphql, query),
    ...rest,
  };
};

export interface LoadedData<TQuery extends OperationType> {
  graphql: GraphQLTaggedNode;
  variables: TQuery["variables"];
  response: TQuery["response"];
}

export const load = async <TQuery extends OperationType>(
  environment: Environment,
  graphql: GraphQLTaggedNode,
  variables: TQuery["variables"] = {},
): Promise<LoadedData<TQuery>> => {
  const response = await fetchQuery<TQuery>(
    environment,
    graphql,
    variables,
  ).toPromise();
  return {
    graphql,
    variables,
    response,
  };
};

export const useLoaded = <TQuery extends OperationType>() => {
  const data = useLoaderData() as LoadedData<TQuery>;
  return data;
};

export const toAbortablePromise = <T>(
  observable: Observable<T>,
  signal: AbortSignal,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    signal.addEventListener("abort", () => {
      reject(new DOMException("Aborted", "AbortError"));
    });
    observable.subscribe({
      next: (data) => {
        resolve(data);
      },
      error: (error: Error) => {
        reject(error);
      },
    });
  });
};

export interface RelayPayloadError {
  message: string;
  path: string[];
  extensions?: Record<string, unknown>;
}

export interface RelayNetworkErrorSource {
  errors: RelayPayloadError[];
}

export interface RelayNetworkError extends Error {
  source: RelayNetworkErrorSource;
  operation: OperationType;
  variables: Record<string, unknown>;
}

export function isRelayNetworkError(
  error: unknown,
): error is RelayNetworkError {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "RelayNetwork"
  );
}

export function collectRelayPayloadErrorCodes<K extends string = string>(
  error: RelayNetworkError,
): Partial<Record<K, RelayPayloadError>> {
  return Object.fromEntries(
    error.source.errors.map((error) => [error.extensions?.["code"], error]),
  );
}

export function relayErrorMessage(error: Error, combine = true) {
  if (isRelayNetworkError(error)) {
    if (error.source.errors.length > 0) {
      if (combine) {
        return error.source.errors.map((error) => error.message).join(". ");
      } else {
        return error.source.errors[0].message;
      }
    }
  }
  return error.message;
}

export function relayErrorExtensions(
  error: Error,
  combine = true,
): Record<string, unknown> {
  if (isRelayNetworkError(error)) {
    if (error.source.errors.length > 0) {
      if (combine) {
        return Object.assign(
          {},
          ...error.source.errors.map((error) => error.extensions || {}),
        );
      } else {
        return error.source.errors[0].extensions || {};
      }
    }
  }
  return {};
}

type ReplaceUploadableWithNull<T extends { [key: string]: unknown }> = {
  [Key in keyof T]: T[Key] extends Uploadable | null
    ? null
    : T[Key] extends Uploadable | null | undefined
      ? null | undefined
      : T[Key] extends { [key: string]: unknown }
        ? ReplaceUploadableWithNull<T[Key]>
        : T[Key];
};

export const extractUploadables = <T extends { [key: string]: unknown }>(
  data: T,
  prefix: string = "variables",
): { variables: ReplaceUploadableWithNull<T>; uploadables: UploadableMap } => {
  const variables = {} as ReplaceUploadableWithNull<T>;
  const uploadables: UploadableMap = {};
  for (const key in data) {
    const value = data[key];
    if (value instanceof File || value instanceof Blob) {
      uploadables[`${prefix}.${key}`] = value as Uploadable;
      variables[key] = null as ReplaceUploadableWithNull<T>[Extract<
        keyof T,
        string
      >];
    } else if (value instanceof Array) {
      const items = [];
      for (const [index, item] of value.entries()) {
        if (item instanceof File || item instanceof Blob) {
          uploadables[`${prefix}.${key}.${index}`] = item as Uploadable;
          items.push(null);
        } else if (isPlainObject(item)) {
          const { variables: nestedVariables, uploadables: nestedUploadables } =
            extractUploadables(
              item as { [key: string]: unknown },
              `${prefix}.${key}.${index}`,
            );
          items.push(nestedVariables);
          Object.assign(uploadables, nestedUploadables);
        } else {
          items.push(item);
        }
      }
      variables[key] = items as ReplaceUploadableWithNull<T>[Extract<
        keyof T,
        string
      >];
    } else if (isPlainObject(value)) {
      const { variables: nestedVariables, uploadables: nestedUploadables } =
        extractUploadables(
          value as { [key: string]: unknown },
          `${prefix}.${key}`,
        );
      variables[key] = nestedVariables as ReplaceUploadableWithNull<T>[Extract<
        keyof T,
        string
      >];
      Object.assign(uploadables, nestedUploadables);
    } else {
      variables[key] = value as ReplaceUploadableWithNull<T>[Extract<
        keyof T,
        string
      >];
    }
  }
  return { variables, uploadables };
};

type ExtendUndefinedWithNull<T extends { [key: string]: unknown }> = {
  [Key in keyof T]: undefined extends T[Key]
    ? null | T[Key]
    : T[Key] extends { [key: string]: unknown }
      ? ExtendUndefinedWithNull<T[Key]>
      : T[Key];
};

export const replaceDirtyUndefinedWithNull = <
  T extends { [key: string]: unknown },
>(
  data: T,
  equalFields: DeepIsEqual<T>,
): ExtendUndefinedWithNull<T> => {
  const output = {} as ExtendUndefinedWithNull<T>;
  for (const key in data) {
    if (data[key] === undefined && equalFields[key] === false) {
      output[key] = null as ExtendUndefinedWithNull<T>[Extract<
        keyof T,
        string
      >];
    } else if (isPlainObject(data[key])) {
      const inner = data[key] as { [key: string]: unknown };
      const dirtyInner = equalFields[key] as unknown as DeepIsEqual<
        typeof inner
      >;
      output[key] = replaceDirtyUndefinedWithNull(
        inner,
        dirtyInner,
      ) as ExtendUndefinedWithNull<T>[Extract<keyof T, string>];
    } else {
      output[key] = data[key] as ExtendUndefinedWithNull<T>[Extract<
        keyof T,
        string
      >];
    }
  }
  return output;
};

export const filterEqualKeys = <T extends { [key: string]: unknown }>(
  data: T,
  equalFields: DeepIsEqual<T>,
): DeepPartialSkipArrayKey<T> => {
  const output = {} as DeepPartialSkipArrayKey<T>;
  for (const key in data) {
    if (isPlainObject(data[key])) {
      const inner = data[key] as { [key: string]: unknown };
      const dirtyInner = equalFields[key] as unknown as DeepIsEqual<
        typeof inner
      >;
      output[key] = replaceDirtyUndefinedWithNull(
        inner,
        dirtyInner,
      ) as DeepPartialSkipArrayKey<T>[Extract<keyof T, string>];
    } else if (!equalFields[key]) {
      output[key] = data[key] as DeepPartialSkipArrayKey<T>[Extract<
        keyof T,
        string
      >];
    }
  }
  return output;
};

export const preprocessEdit = <
  T extends { [key: string]: unknown },
  T2 extends DeepPartial<T>,
>(
  data: T,
  defaultValues: T2 = {} as T2,
): ExtendUndefinedWithNull<DeepPartialSkipArrayKey<T>> => {
  const dirtyFields = deepIsEqual(data, defaultValues);
  return replaceDirtyUndefinedWithNull(
    filterEqualKeys(data, dirtyFields),
    dirtyFields as DeepIsEqual<DeepPartialSkipArrayKey<T>>,
  );
};

export function toPromise<T>(
  value: Subscribable<T>,
  abort: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let closed = false;
    const subscription = value.subscribe({
      next(observed) {
        resolve(observed);
        closed = true;
      },
      error(error: Error) {
        reject(error);
        closed = true;
      },
      complete() {
        if (!closed) {
          reject(new Error("no data"));
        }
        closed = true;
      },
      get closed() {
        return closed;
      },
    });
    abort.addEventListener("abort", () => {
      closed = true;
      subscription.unsubscribe();
    });
  });
}

export async function readFragmentData<K extends KeyType>(
  environment: Environment,
  fragment: GraphQLTaggedNode,
  fragmentKey: K,
): Promise<KeyTypeData<K>> {
  const fragmentReader = getFragment(fragment);
  const fragmentSelector = getSelector(fragmentReader, fragmentKey);
  invariant(
    fragmentSelector.kind === "SingularReaderSelector",
    "Expected a singular fragment reader",
  );

  let fragmentSnapshot = environment.lookup(fragmentSelector);
  while (fragmentSnapshot.isMissingData) {
    const operations = getPendingOperationsForFragment(
      environment,
      fragmentReader,
      fragmentSelector.owner,
    );
    invariant(!!operations);
    await operations.promise;
    fragmentSnapshot = environment.lookup(fragmentSelector);
  }

  return fragmentSnapshot.data;
}

export async function refetchFragmentData<
  K extends KeyType<{ id: ID }>,
  Op extends OperationType & { response: { node: K } },
>(
  environment: Environment,
  fragment: GraphQLTaggedNode,
  fragmentKey: K,
  variables: Omit<Op["variables"], "id">,
): Promise<KeyTypeData<K>> {
  const fragmentReader = getRefetchableFragment(fragment);
  invariant(fragmentReader !== null, "Fragment is not refetchable");
  invariant(
    typeof fragmentReader.metadata.refetch.operation !== "string",
    "Fragment refetch query hasn't been compiled",
  );

  const fragmentData = await readFragmentData(
    environment,
    fragment,
    fragmentKey,
  );
  if (!fragmentData) {
    throw new Error("Fragment has no data");
  }

  const response = await fetchQuery<Op>(
    environment,
    fragmentReader.metadata.refetch.operation,
    {
      ...variables,
      id: fragmentData.id,
    },
  ).toPromise();
  if (!response) {
    throw new Error("Refetch fragment failed");
  }

  return readFragmentData(environment, fragment, response.node);
}
